'use client'
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { LiveSession, RosterEntry } from '@gymboard/types'
import QrDisplay from '../components/qr/QrDisplay'
import { formatClock } from '../lib/utils'
import { getTenantInfo } from '../lib/storage'
import ScreenBadge from '../components/ScreenBadge'

interface Props {
  session: LiveSession | null
  qrToken: string | null
  qrSignature: string | null
  qrExpiresAt: number | null
  roster: RosterEntry[]
}

export default function PreClassScreen({
  session,
  qrToken,
  qrSignature,
  qrExpiresAt,
  roster,
}: Props) {
  const [clock, setClock] = useState(() => formatClock(new Date()))
  const tenantInfo = getTenantInfo()

  useEffect(() => {
    const interval = setInterval(() => setClock(formatClock(new Date())), 1000)
    return () => clearInterval(interval)
  }, [])

  const className = session?.classInstance?.template?.name ?? 'Class'
  const coachName = session?.classInstance?.coach
    ? `${(session.classInstance.coach as any).firstName} ${(session.classInstance.coach as any).lastName}`
    : null
  const durationMins =
    (session?.classInstance as any)?.durationMins ??
    session?.classInstance?.template?.durationMins

  return (
    <div className="w-screen h-screen relative bg-[#0E1022] overflow-hidden flex">
      {/* Screen badge */}
      <div className="absolute top-5 right-5 z-30">
        <ScreenBadge className="text-white/30 text-[10px]" />
      </div>

      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 15% 50%, rgba(201,58,48,0.06) 0%, transparent 60%)',
          }}
        />
      </div>

      {/* ── Left column: class info + QR ─────────────────────────────────── */}
      <div className="relative z-10 flex flex-col justify-center pl-16 pr-8 w-[45%]">
        {/* Gym name */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-white/30 text-sm font-semibold tracking-[0.25em] uppercase mb-8"
        >
          {tenantInfo?.name ?? 'Agaru'}
        </motion.div>

        {/* Class meta */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <p className="text-orange-300/70 text-sm font-semibold tracking-[0.2em] uppercase mb-2">
            Up Next
          </p>
          <h1 className="text-white text-6xl font-black leading-none mb-4">{className}</h1>

          <div className="flex flex-wrap items-center gap-4 text-white/45 text-base mb-10">
            {coachName && (
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {coachName}
              </span>
            )}
            {durationMins && (
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {durationMins} min
              </span>
            )}
          </div>
        </motion.div>

        {/* QR Check-in */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <QrDisplay
            token={qrToken}
            signature={qrSignature}
            expiresAt={qrExpiresAt}
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-white/20 text-sm mt-8"
        >
          Waiting for coach to start session…
        </motion.p>
      </div>

      {/* Divider */}
      <div className="relative z-10 self-stretch w-px bg-white/5 my-12" />

      {/* ── Right column: clock + checked-in list ────────────────────────── */}
      <div className="relative z-10 flex flex-col flex-1 pl-10 pr-16 py-12">
        {/* Clock */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-right mb-10"
        >
          <div className="text-7xl font-black text-white/70 tabular-nums tracking-tight">
            {clock}
          </div>
          <div className="text-white/25 text-sm tracking-widest uppercase mt-1">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </div>
        </motion.div>

        {/* Checked-in athletes */}
        <div className="flex-1 overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <p className="text-white/35 text-sm font-semibold tracking-[0.15em] uppercase">
              Checked In
            </p>
            <AnimatePresence mode="popLayout">
              <motion.span
                key={roster.length}
                initial={{ scale: 1.4, color: '#FF7A6E' }}
                animate={{ scale: 1, color: '#E0493E' }}
                transition={{ duration: 0.4 }}
                className="font-bold text-lg"
              >
                {roster.length}
              </motion.span>
            </AnimatePresence>
          </div>

          {roster.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/15 text-xl">No one checked in yet</p>
              <p className="text-white/10 text-sm mt-2">Scan the QR code to join</p>
            </div>
          ) : (
            <div className="space-y-2 overflow-y-auto max-h-[55vh] pr-2 scrollbar-none">
              <AnimatePresence initial={false}>
                {roster.map((entry, i) => (
                  <motion.div
                    key={entry.athleteId}
                    initial={{ opacity: 0, x: 40, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                    layout
                    className="flex items-center gap-3 bg-white/3 border border-white/5 rounded-xl px-4 py-3"
                  >
                    {/* Avatar or initials */}
                    {entry.avatarUrl ? (
                      <img
                        src={entry.avatarUrl}
                        alt={entry.athleteName}
                        className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-orange-300 text-sm font-bold">
                          {entry.athleteName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}

                    <span className="text-white/80 font-medium text-base flex-1 truncate">
                      {entry.athleteName}
                    </span>

                    {/* Position badge */}
                    <span className="text-white/20 text-xs tabular-nums w-5 text-right">
                      #{i + 1}
                    </span>

                    {/* Green tick */}
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.1, type: 'spring' }}
                      className="text-green-400 text-sm"
                    >
                      ✓
                    </motion.span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
